import { useState, useEffect, useRef } from "react";

const API = "https://vpnnull-production.up.railway.app";

const PLANS = [
  { id: "monthly", name: "Monthly", price: "7.99", period: "/mo", billed: null, devices: 1, features: ["1 Device","50+ Server Locations","No-Logs Policy","Kill Switch","24/7 Support"], cta: "Start Monthly", highlight: false },
  { id: "annual", name: "Annual", price: "3.99", period: "/mo", billed: "Billed $47.88 / year — save 50%", devices: 5, features: ["5 Devices","50+ Server Locations","No-Logs Policy","Kill Switch","Split Tunneling","Priority Support"], cta: "Best Deal — Start Now", highlight: true, badge: "50% OFF" },
  { id: "lifetime", name: "Lifetime", price: "69", period: "once", billed: "One payment, forever", devices: 10, features: ["10 Devices","50+ Server Locations","No-Logs Policy","Kill Switch","Split Tunneling","Dedicated IP Option","All Future Features"], cta: "Get Lifetime Access", highlight: false },
];

const SERVERS = [
  { city:"New York", cc:"US", flag:"🇺🇸", ping:12, load:34 },
  { city:"London", cc:"GB", flag:"🇬🇧", ping:18, load:22 },
  { city:"Frankfurt", cc:"DE", flag:"🇩🇪", ping:21, load:41 },
  { city:"Amsterdam", cc:"NL", flag:"🇳🇱", ping:19, load:26 },
  { city:"Toronto", cc:"CA", flag:"🇨🇦", ping:28, load:37 },
  { city:"Singapore", cc:"SG", flag:"🇸🇬", ping:110, load:29 },
  { city:"Tokyo", cc:"JP", flag:"🇯🇵", ping:89, load:18 },
  { city:"Sydney", cc:"AU", flag:"🇦🇺", ping:145, load:15 },
  { city:"Sao Paulo", cc:"BR", flag:"🇧🇷", ping:160, load:12 },
  { city:"Stockholm", cc:"SE", flag:"🇸🇪", ping:22, load:19 },
  { city:"Zurich", cc:"CH", flag:"🇨🇭", ping:24, load:31 },
  { city:"Paris", cc:"FR", flag:"🇫🇷", ping:20, load:28 },
];

const FAQS = [
  ["Do you keep any logs?","Zero. Our server architecture makes it technically impossible to log user activity. We store only what's needed for your account: email, hashed password, and WireGuard public key."],
  ["What protocol do you use?","WireGuard — the fastest, most modern VPN protocol available. It's 3x faster than OpenVPN and uses state-of-the-art cryptography."],
  ["Can I cancel anytime?","Yes, monthly and annual plans can be cancelled anytime from your dashboard. You keep access until the end of your billing period."],
  ["How many devices can I use?","Monthly: 1 device. Annual: 5 devices. Lifetime: 10 devices. All simultaneously."],
  ["Where is VPNNull incorporated?","Panama — chosen specifically because it has no mandatory data retention laws and falls outside 5/9/14 Eyes alliances."],
];

// ── ICONS ──
const Icon = ({ d, size=20, stroke=2, fill="none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {typeof d === "string" ? <path d={d}/> : d}
  </svg>
);
const ShieldIcon = ({size=20}) => <Icon size={size} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>;
const CheckIcon = ({size=16}) => <Icon size={size} stroke={2.5} d="M20 6 9 17 4 12"/>;
const ZapIcon = ({size=20}) => <Icon size={size} fill="currentColor" stroke="none" d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>;
const GlobeIcon = ({size=20}) => <Icon size={size} d={<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>}/>;
const LockIcon = ({size=20}) => <Icon size={size} d={<><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>}/>;
const EyeOffIcon = ({size=20}) => <Icon size={size} d={<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>}/>;
const ArrowRight = ({size=16}) => <Icon size={size} d="m9 18 6-6-6-6"/>;
const DownloadIcon = ({size=16}) => <Icon size={size} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>;
const XIcon = ({size=20}) => <Icon size={size} d="M18 6 6 18M6 6l12 12"/>;
const MenuIcon = ({size=22}) => <Icon size={size} d={<><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}/>;
const ChevronDown = ({size=16}) => <Icon size={size} d="m6 9 6 6 6-6"/>;
const ActivityIcon = ({size=20}) => <Icon size={size} d="M22 12h-4l-3 9L9 3l-3 9H2"/>;

// ── STYLES ──
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--bg:#03040a;--s1:#080b14;--s2:#0e1220;--s3:#141926;--bd:rgba(255,255,255,0.065);--bd2:rgba(255,255,255,0.12);--a:#22d48f;--ab:rgba(34,212,143,0.12);--ab2:rgba(34,212,143,0.06);--txt:#eef0f8;--mu:#5a6175;--mu2:#8491a8;--r:rgba(255,80,80,0.85)}
html{scroll-behavior:smooth}
body{background:var(--bg);color:var(--txt);font-family:'DM Sans',sans-serif;overflow-x:hidden;-webkit-font-smoothing:antialiased}
::selection{background:var(--ab);color:var(--a)}
.nav{position:fixed;top:0;left:0;right:0;z-index:200;display:flex;align-items:center;justify-content:space-between;padding:0 44px;height:64px;background:rgba(3,4,10,0.82);backdrop-filter:blur(24px);border-bottom:1px solid var(--bd)}
.nav-logo{display:flex;align-items:center;gap:9px;cursor:pointer;text-decoration:none;user-select:none}
.nav-logo-mark{width:32px;height:32px;background:var(--a);border-radius:8px;display:flex;align-items:center;justify-content:center;color:#000;flex-shrink:0}
.nav-logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:18px;letter-spacing:-.5px;color:var(--txt)}
.nav-links{display:flex;gap:2px;list-style:none}
.nav-links button{background:none;border:none;color:var(--mu2);font-family:'DM Sans',sans-serif;font-size:14px;padding:7px 14px;border-radius:7px;cursor:pointer;transition:all .2s}
.nav-links button:hover{color:var(--txt);background:rgba(255,255,255,.05)}
.nav-right{display:flex;align-items:center;gap:10px}
.btn-ghost{background:none;border:1px solid var(--bd2);color:var(--mu2);padding:7px 18px;border-radius:8px;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:14px;transition:all .2s}
.btn-ghost:hover{color:var(--txt);background:rgba(255,255,255,.05)}
.btn-green{background:var(--a);border:none;color:#000;padding:8px 20px;border-radius:8px;cursor:pointer;font-family:'DM Sans',sans-serif;font-weight:600;font-size:14px;transition:all .2s;display:flex;align-items:center;gap:6px}
.btn-green:hover{background:#28f4a6;box-shadow:0 0 24px rgba(34,212,143,.35);transform:translateY(-1px)}
.hamburger{display:none;background:none;border:none;color:var(--txt);cursor:pointer;padding:4px}
.hero{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:100px 24px 80px;text-align:center;position:relative;overflow:hidden}
.hero-bg{position:absolute;inset:0;z-index:0}
.hero-blob1{position:absolute;width:700px;height:700px;left:50%;top:-100px;transform:translateX(-50%);border-radius:50%;background:radial-gradient(ellipse,rgba(34,212,143,.1) 0%,transparent 65%);animation:breathe 8s ease-in-out infinite}
.hero-blob2{position:absolute;width:400px;height:400px;right:-100px;bottom:0;border-radius:50%;background:radial-gradient(ellipse,rgba(80,120,255,.07) 0%,transparent 65%);animation:breathe2 10s ease-in-out infinite}
.hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px);background-size:52px 52px;mask-image:radial-gradient(ellipse 80% 60% at 50% 0%,black 20%,transparent 80%)}
@keyframes breathe{0%,100%{transform:translateX(-50%) scale(1)}50%{transform:translateX(-50%) scale(1.12)}}
@keyframes breathe2{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}
.hero-content{position:relative;z-index:1;max-width:820px}
.hero-eyebrow{display:inline-flex;align-items:center;gap:8px;background:rgba(34,212,143,.08);border:1px solid rgba(34,212,143,.2);color:var(--a);padding:5px 14px 5px 8px;border-radius:100px;font-size:12.5px;font-weight:600;letter-spacing:.3px;margin-bottom:30px}
.hero-eyebrow-dot{width:6px;height:6px;background:var(--a);border-radius:50%;animation:pulse 2s infinite}
.hero-h1{font-family:'Syne',sans-serif;font-weight:800;font-size:clamp(44px,8vw,88px);line-height:1.0;letter-spacing:-3px;margin-bottom:26px}
.hero-h1 .line2{color:var(--a);display:block}
.hero-p{color:var(--mu2);font-size:clamp(16px,2vw,19px);line-height:1.75;max-width:540px;margin:0 auto 40px;font-weight:300}
.hero-cta{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;margin-bottom:70px}
.hero-btn-main{padding:14px 34px;background:var(--a);color:#000;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:16px;cursor:pointer;transition:all .25s;display:flex;align-items:center;gap:8px}
.hero-btn-main:hover{background:#28f4a6;box-shadow:0 8px 32px rgba(34,212,143,.4);transform:translateY(-2px)}
.hero-btn-sec{padding:14px 28px;background:none;border:1px solid var(--bd2);color:var(--txt);border-radius:10px;font-family:'DM Sans',sans-serif;font-size:16px;cursor:pointer;transition:all .25s}
.hero-btn-sec:hover{border-color:rgba(255,255,255,.25);background:rgba(255,255,255,.04)}
.hero-stats{display:flex;justify-content:center;gap:56px;flex-wrap:wrap}
.hero-stat-num{font-family:'Syne',sans-serif;font-size:clamp(24px,4vw,34px);font-weight:800;letter-spacing:-1px}
.hero-stat-label{font-size:13px;color:var(--mu);margin-top:3px}
.sec{padding:100px 40px;max-width:1180px;margin:0 auto}
.sec-label{font-size:11px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--a);margin-bottom:12px}
.sec-h2{font-family:'Syne',sans-serif;font-weight:800;font-size:clamp(30px,4vw,48px);letter-spacing:-1.5px;line-height:1.05;margin-bottom:14px}
.sec-sub{color:var(--mu2);font-size:17px;max-width:500px;line-height:1.7;font-weight:300}
.divider{border:none;border-top:1px solid var(--bd);margin:0}
.feat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px;margin-top:56px}
.feat-card{background:var(--s1);border:1px solid var(--bd);border-radius:16px;padding:30px;transition:all .3s;position:relative;overflow:hidden}
.feat-card:hover{border-color:rgba(34,212,143,.22);transform:translateY(-4px);box-shadow:0 24px 60px rgba(0,0,0,.45)}
.feat-icon{width:44px;height:44px;background:var(--ab);border-radius:11px;display:flex;align-items:center;justify-content:center;color:var(--a);margin-bottom:18px}
.feat-card h3{font-family:'Syne',sans-serif;font-size:17px;font-weight:700;margin-bottom:8px}
.feat-card p{color:var(--mu2);font-size:14px;line-height:1.7;font-weight:300}
.srv-bg{background:var(--s1);border-top:1px solid var(--bd);border-bottom:1px solid var(--bd)}
.srv-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;margin-top:44px}
.srv-card{background:var(--bg);border:1px solid var(--bd);border-radius:12px;padding:16px 18px;display:flex;align-items:center;gap:13px;transition:all .2s}
.srv-card:hover{border-color:rgba(34,212,143,.18);background:rgba(34,212,143,.025)}
.srv-flag{font-size:26px;line-height:1;flex-shrink:0}
.srv-info{flex:1;min-width:0}
.srv-city{font-size:14px;font-weight:600}
.srv-cc{font-size:11px;color:var(--mu);margin-top:1px}
.srv-meta{text-align:right;flex-shrink:0}
.srv-ping{font-size:12px;color:var(--a);font-weight:700}
.srv-load{font-size:10px;color:var(--mu);margin-top:2px}
.srv-bar{width:52px;height:3px;background:var(--bd2);border-radius:2px;margin-top:4px;overflow:hidden}
.srv-fill{height:100%;background:var(--a);border-radius:2px}
.pricing-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:22px;margin-top:56px;align-items:start}
.plan{background:var(--s1);border:1px solid var(--bd);border-radius:20px;padding:36px;position:relative;transition:all .3s}
.plan.hi{background:var(--s2);border-color:rgba(34,212,143,.35);box-shadow:0 0 70px rgba(34,212,143,.09)}
.plan-badge{position:absolute;top:-13px;left:50%;transform:translateX(-50%);background:var(--a);color:#000;font-size:11px;font-weight:800;padding:4px 14px;border-radius:100px;letter-spacing:.5px;white-space:nowrap}
.plan-name{font-size:12px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--mu);margin-bottom:18px}
.plan-price{display:flex;align-items:baseline;gap:3px;margin-bottom:6px}
.plan-dollar{font-family:'Syne',sans-serif;font-size:44px;font-weight:800;letter-spacing:-2px;line-height:1}
.plan-per{font-size:14px;color:var(--mu2);margin-left:2px}
.plan-billed{font-size:12.5px;color:var(--mu);margin-bottom:28px;min-height:18px}
.plan-feats{list-style:none;display:flex;flex-direction:column;gap:11px;margin-bottom:30px}
.plan-feat{display:flex;align-items:center;gap:9px;font-size:14px;color:var(--mu2)}
.plan-feat-ck{color:var(--a);flex-shrink:0}
.plan-btn{width:100%;padding:12px;border-radius:10px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:14.5px;cursor:pointer;transition:all .2s}
.plan-btn-o{background:none;border:1px solid var(--bd2);color:var(--txt)}
.plan-btn-o:hover{border-color:rgba(255,255,255,.25);background:rgba(255,255,255,.04)}
.plan-btn-g{background:var(--a);border:none;color:#000}
.plan-btn-g:hover{background:#28f4a6;box-shadow:0 6px 24px rgba(34,212,143,.3)}
.faq-list{margin-top:48px;display:flex;flex-direction:column;gap:12px;max-width:720px}
.faq-item{background:var(--s1);border:1px solid var(--bd);border-radius:14px;overflow:hidden;transition:border-color .2s}
.faq-item.open{border-color:rgba(34,212,143,.2)}
.faq-q{width:100%;background:none;border:none;color:var(--txt);display:flex;align-items:center;justify-content:space-between;padding:20px 24px;cursor:pointer;text-align:left;gap:16px;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:500}
.faq-chevron{color:var(--mu);transition:transform .3s;flex-shrink:0}
.faq-item.open .faq-chevron{transform:rotate(180deg);color:var(--a)}
.faq-a{padding:0 24px 20px;color:var(--mu2);font-size:14px;line-height:1.75;font-weight:300;display:none}
.faq-item.open .faq-a{display:block}
.overlay{position:fixed;inset:0;background:rgba(0,0,0,.75);backdrop-filter:blur(10px);z-index:500;display:flex;align-items:center;justify-content:center;padding:20px;animation:fadeIn .2s}
.modal{background:var(--s1);border:1px solid var(--bd2);border-radius:22px;padding:40px;width:100%;max-width:420px;position:relative;animation:slideUp .25s}
.modal-x{position:absolute;top:14px;right:14px;background:none;border:none;color:var(--mu);cursor:pointer;padding:6px;border-radius:8px;transition:all .2s}
.modal-x:hover{color:var(--txt);background:rgba(255,255,255,.07)}
.modal-logo{display:flex;align-items:center;gap:8px;margin-bottom:24px}
.modal-logo-m{width:28px;height:28px;background:var(--a);border-radius:7px;display:flex;align-items:center;justify-content:center;color:#000}
.modal-logo-t{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
.modal h2{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;letter-spacing:-.5px;margin-bottom:4px}
.modal-sub{color:var(--mu2);font-size:13.5px;margin-bottom:28px}
.tabs{display:flex;background:var(--bg);border-radius:10px;padding:4px;gap:4px;margin-bottom:26px}
.tab{flex:1;padding:8px;background:none;border:none;color:var(--mu);font-family:'DM Sans',sans-serif;font-size:13.5px;font-weight:500;cursor:pointer;border-radius:7px;transition:all .2s}
.tab.on{background:var(--s2);color:var(--txt);box-shadow:0 2px 8px rgba(0,0,0,.4)}
.field{margin-bottom:14px}
.field label{display:block;font-size:12.5px;font-weight:500;color:var(--mu);margin-bottom:5px;letter-spacing:.3px}
.field input{width:100%;background:var(--bg);border:1px solid var(--bd);border-radius:8px;padding:10px 13px;color:var(--txt);font-family:'DM Sans',sans-serif;font-size:14px;outline:none;transition:border-color .2s}
.field input:focus{border-color:var(--a)}
.field input::placeholder{color:var(--mu)}
.form-btn{width:100%;margin-top:6px;padding:12px;background:var(--a);border:none;border-radius:9px;color:#000;font-family:'DM Sans',sans-serif;font-weight:700;font-size:15px;cursor:pointer;transition:all .2s}
.form-btn:hover{background:#28f4a6}
.form-btn:disabled{opacity:.55;cursor:not-allowed}
.form-note{font-size:11.5px;color:var(--mu);text-align:center;margin-top:12px}
.success-wrap{text-align:center;padding:12px 0}
.success-ring{width:64px;height:64px;background:var(--ab);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 18px;color:var(--a)}
.success-wrap h3{font-family:'Syne',sans-serif;font-size:21px;font-weight:800;margin-bottom:7px}
.success-wrap p{color:var(--mu2);font-size:14px}
.error-msg{background:rgba(255,80,80,.1);border:1px solid rgba(255,80,80,.2);color:var(--r);padding:10px 13px;border-radius:8px;font-size:13px;margin-bottom:12px}
.dash{min-height:100vh;padding:88px 40px 60px;max-width:1100px;margin:0 auto}
.dash-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:36px;flex-wrap:wrap;gap:16px}
.dash-title h1{font-family:'Syne',sans-serif;font-size:clamp(24px,3vw,32px);font-weight:800;letter-spacing:-1px;margin-bottom:3px}
.dash-title p{color:var(--mu2);font-size:14px}
.btn-sm-ghost{background:none;border:1px solid var(--bd2);color:var(--mu2);padding:7px 16px;border-radius:8px;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:13px;transition:all .2s}
.btn-sm-ghost:hover{color:var(--txt);border-color:rgba(255,255,255,.2)}
.vpn-card{background:var(--s1);border:1px solid var(--bd);border-radius:20px;padding:32px;margin-bottom:22px;position:relative;overflow:hidden}
.vpn-card.on{border-color:rgba(34,212,143,.25);box-shadow:0 0 60px rgba(34,212,143,.07)}
.vpn-glow{position:absolute;top:0;left:50%;transform:translateX(-50%);width:300px;height:2px;background:linear-gradient(90deg,transparent,var(--a),transparent);opacity:0;transition:opacity .5s}
.vpn-card.on .vpn-glow{opacity:1}
.vpn-top{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;margin-bottom:28px}
.vpn-status{display:flex;align-items:center;gap:14px}
.vpn-dot{width:12px;height:12px;border-radius:50%;background:var(--mu);flex-shrink:0;transition:all .4s}
.vpn-dot.on{background:var(--a);box-shadow:0 0 14px rgba(34,212,143,.7);animation:pulse 2s infinite}
.vpn-label{font-family:'Syne',sans-serif;font-size:18px;font-weight:700}
.vpn-sublabel{font-size:12.5px;color:var(--mu2);margin-top:2px}
.toggle-wrap{display:flex;align-items:center;gap:12px}
.toggle-text{font-size:13px;color:var(--mu2)}
.toggle{width:56px;height:30px;border-radius:15px;border:none;cursor:pointer;position:relative;transition:background .3s;flex-shrink:0}
.toggle.on{background:var(--a)}
.toggle.off{background:var(--s3);border:1px solid var(--bd2)}
.toggle-thumb{position:absolute;top:3px;width:24px;height:24px;background:#fff;border-radius:50%;transition:left .3s;box-shadow:0 2px 6px rgba(0,0,0,.4)}
.toggle.on .toggle-thumb{left:29px;background:#000}
.toggle.off .toggle-thumb{left:3px}
.vpn-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}
.vpn-m{background:var(--bg);border-radius:12px;padding:16px}
.vpn-m-label{font-size:10.5px;color:var(--mu);text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px}
.vpn-m-val{font-family:'Syne',sans-serif;font-size:18px;font-weight:800}
.vpn-m-val.g{color:var(--a)}
.dash-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.dc{background:var(--s1);border:1px solid var(--bd);border-radius:16px;padding:24px}
.dc h3{font-family:'Syne',sans-serif;font-size:15px;font-weight:700;margin-bottom:18px;letter-spacing:-.3px;display:flex;align-items:center;gap:8px}
.sp-list{display:flex;flex-direction:column;gap:8px}
.sp-opt{display:flex;align-items:center;gap:11px;padding:11px 13px;border-radius:10px;background:var(--bg);border:1px solid var(--bd);cursor:pointer;transition:all .2s}
.sp-opt:hover,.sp-opt.active{border-color:rgba(34,212,143,.3);background:rgba(34,212,143,.04)}
.sp-flag{font-size:18px;flex-shrink:0}
.sp-info{flex:1;min-width:0}
.sp-city{font-size:13.5px;font-weight:500}
.sp-ping{font-size:11px;color:var(--mu2)}
.sp-ck{color:var(--a)}
.sub-rows{display:flex;flex-direction:column;gap:8px}
.sub-row{display:flex;justify-content:space-between;align-items:center;padding:10px 13px;background:var(--bg);border-radius:9px;font-size:13px}
.sub-row-k{color:var(--mu2)}
.sub-row-v{font-weight:500}
.sub-row-v.g{color:var(--a)}
.dl-box{background:var(--bg);border:1px dashed rgba(34,212,143,.25);border-radius:12px;padding:22px;text-align:center;margin-top:16px}
.dl-box p{color:var(--mu2);font-size:13px;margin-bottom:14px;line-height:1.6}
.dl-btn{display:inline-flex;align-items:center;gap:7px;background:var(--ab);border:1px solid rgba(34,212,143,.25);color:var(--a);padding:9px 20px;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:13.5px;font-weight:600;cursor:pointer;transition:all .2s}
.dl-btn:hover{background:rgba(34,212,143,.18);box-shadow:0 4px 16px rgba(34,212,143,.2)}
.upgrade-banner{background:linear-gradient(135deg,rgba(34,212,143,.1),rgba(34,212,143,.05));border:1px solid rgba(34,212,143,.2);border-radius:16px;padding:28px;text-align:center;margin-bottom:22px}
.upgrade-banner h3{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;margin-bottom:8px}
.upgrade-banner p{color:var(--mu2);font-size:14px;margin-bottom:20px}
.upgrade-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:var(--a);border:none;border-radius:10px;color:#000;font-family:'DM Sans',sans-serif;font-weight:700;font-size:15px;cursor:pointer;transition:all .2s}
.upgrade-btn:hover{background:#28f4a6}
.footer{border-top:1px solid var(--bd);padding:64px 40px 36px}
.footer-inner{max-width:1180px;margin:0 auto}
.footer-top{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:40px;margin-bottom:48px}
.footer-brand-logo{display:flex;align-items:center;gap:8px;margin-bottom:14px}
.footer-brand-lm{width:28px;height:28px;background:var(--a);border-radius:7px;display:flex;align-items:center;justify-content:center;color:#000}
.footer-brand-lt{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
.footer-brand-p{color:var(--mu);font-size:13.5px;line-height:1.7;max-width:260px;font-weight:300}
.footer-col h4{font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--mu);margin-bottom:16px}
.footer-col ul{list-style:none;display:flex;flex-direction:column;gap:9px}
.footer-col-btn{background:none;border:none;color:var(--mu2);font-size:13.5px;cursor:pointer;padding:0;text-align:left;transition:color .2s;font-family:'DM Sans',sans-serif}
.footer-col-btn:hover{color:var(--txt)}
.footer-bottom{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;padding-top:24px;border-top:1px solid var(--bd)}
.footer-bottom-t{font-size:12.5px;color:var(--mu)}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1;box-shadow:0 0 14px rgba(34,212,143,.7)}50%{opacity:.7;box-shadow:0 0 6px rgba(34,212,143,.3)}}
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin .8s linear infinite}
@media(max-width:900px){.nav-links,.nav-right .btn-ghost{display:none}.hamburger{display:block}.sec{padding:70px 20px}.footer-top{grid-template-columns:1fr 1fr}.vpn-metrics{grid-template-columns:1fr 1fr}.dash-grid{grid-template-columns:1fr}.nav{padding:0 20px}.dash{padding:80px 20px 50px}}
@media(max-width:580px){.hero-stats{gap:28px}.footer-top{grid-template-columns:1fr}.pricing-grid{grid-template-columns:1fr}}
`;

// ── HELPERS ──
function getToken() { try { return localStorage.getItem("vpnnull_token"); } catch { return null; } }
function setToken(t) { try { localStorage.setItem("vpnnull_token", t); } catch {} }
function clearToken() { try { localStorage.removeItem("vpnnull_token"); localStorage.removeItem("vpnnull_user"); } catch {} }
function getUser() { try { const u = localStorage.getItem("vpnnull_user"); return u ? JSON.parse(u) : null; } catch { return null; } }
function setUser(u) { try { localStorage.setItem("vpnnull_user", JSON.stringify(u)); } catch {} }

function Spinner({ text = "Loading..." }) {
  return (
    <span style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
      <svg className="spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
      {text}
    </span>
  );
}

// ── AUTH MODAL ──
function AuthModal({ tab: initTab = "signup", onClose, onLogin }) {
  const [tab, setTab] = useState(initTab);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("annual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async () => {
    setError("");
    if (!email || !pw) return setError("Please fill in all fields.");
    setLoading(true);

    try {
      const endpoint = tab === "signup" ? "/api/auth/register" : "/api/auth/login";
      const body = tab === "signup" ? { email, password: pw, name } : { email, password: pw };

      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Something went wrong.");
        setLoading(false);
        return;
      }

      // Save token and user
      setToken(data.token);
      setUser(data.user);

      if (tab === "signup") {
        // Go to Stripe checkout
        setSuccess("Account created! Redirecting to checkout...");
        const checkoutRes = await fetch(`${API}/api/subscriptions/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${data.token}`,
          },
          body: JSON.stringify({ plan }),
        });
        const checkoutData = await checkoutRes.json();
        if (checkoutData.url) {
          window.location.href = checkoutData.url;
        } else {
          // No Stripe yet — go straight to dashboard
          onLogin(data.user);
        }
      } else {
        onLogin(data.user);
      }
    } catch (err) {
      setError("Connection error. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <button className="modal-x" onClick={onClose}><XIcon /></button>
        <div className="modal-logo">
          <div className="modal-logo-m"><ShieldIcon size={14}/></div>
          <span className="modal-logo-t">VPNNull</span>
        </div>
        {success ? (
          <div className="success-wrap">
            <div className="success-ring"><CheckIcon size={28}/></div>
            <h3>Account created!</h3>
            <p>{success}</p>
          </div>
        ) : (
          <>
            <h2>{tab === "signup" ? "Create your account" : "Sign in"}</h2>
            <p className="modal-sub">{tab === "signup" ? "Start your private internet experience" : "Back to zero logs, full privacy"}</p>
            <div className="tabs">
              <button className={`tab ${tab === "signup" ? "on" : ""}`} onClick={() => { setTab("signup"); setError(""); }}>Sign Up</button>
              <button className={`tab ${tab === "login" ? "on" : ""}`} onClick={() => { setTab("login"); setError(""); }}>Log In</button>
            </div>
            {error && <div className="error-msg">{error}</div>}
            {tab === "signup" && (
              <div className="field">
                <label>NAME</label>
                <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
              </div>
            )}
            <div className="field">
              <label>EMAIL</label>
              <input type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} />
            </div>
            <div className="field">
              <label>PASSWORD</label>
              <input type="password" placeholder={tab === "signup" ? "Create a password" : "Your password"} value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} />
            </div>
            {tab === "signup" && (
              <div className="field">
                <label>PLAN</label>
                <div style={{ display:"flex", gap:7 }}>
                  {PLANS.map(p => (
                    <button key={p.id} onClick={() => setPlan(p.id)} style={{
                      flex:1, padding:"9px 4px", borderRadius:8, cursor:"pointer", transition:"all .2s",
                      background: plan === p.id ? "var(--ab)" : "var(--bg)",
                      border: plan === p.id ? "1px solid rgba(34,212,143,.35)" : "1px solid var(--bd)",
                      color: plan === p.id ? "var(--a)" : "var(--mu2)",
                      fontSize:12, fontFamily:"DM Sans,sans-serif", fontWeight:600, lineHeight:1.4
                    }}>
                      {p.name}<br/><span style={{opacity:.75}}>${p.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button className="form-btn" disabled={loading} onClick={submit}>
              {loading ? <Spinner text={tab === "signup" ? "Creating account..." : "Signing in..."} /> : tab === "signup" ? "Create Account & Pay" : "Sign In"}
            </button>
            <p className="form-note">Secured by Stripe. Cancel anytime. No logs ever.</p>
          </>
        )}
      </div>
    </div>
  );
}

// ── LANDING ──
function Landing({ openAuth, scrollTo }) {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <>
      <section className="hero" id="home">
        <div className="hero-bg">
          <div className="hero-blob1"/>
          <div className="hero-blob2"/>
          <div className="hero-grid"/>
        </div>
        <div className="hero-content">
          <div className="hero-eyebrow">
            <div className="hero-eyebrow-dot"/>
            WireGuard Protocol · AES-256 · Panama Jurisdiction
          </div>
          <h1 className="hero-h1">
            Your internet.
            <span className="line2">Zero trace.</span>
          </h1>
          <p className="hero-p">VPNNull encrypts everything, retains nothing. No activity logs, no IP logs, no DNS logs — not because we promise it, but because our architecture makes it impossible.</p>
          <div className="hero-cta">
            <button className="hero-btn-main" onClick={() => openAuth("signup")}>Get Protected Now <ArrowRight size={16}/></button>
            <button className="hero-btn-sec" onClick={() => scrollTo("pricing")}>See Plans</button>
          </div>
          <div className="hero-stats">
            {[["50+","Servers"],["10 Gbps","Network"],["0","Logs"],["99.9%","Uptime"],["256-bit","Encryption"]].map(([n,l]) => (
              <div key={l} style={{textAlign:"center"}}>
                <div className="hero-stat-num">{n}</div>
                <div className="hero-stat-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <hr className="divider"/>
      <section id="features">
        <div className="sec">
          <div className="sec-label">Why VPNNull</div>
          <h2 className="sec-h2">Privacy is not a feature.<br/>It is the foundation.</h2>
          <p className="sec-sub">Every decision — from server locations to database schema — is made with your privacy as the constraint, not the afterthought.</p>
          <div className="feat-grid">
            {[
              [<EyeOffIcon size={20}/>, "Verified No-Logs", "We do not collect browsing history, IP addresses, DNS queries, or session data. Independently audited."],
              [<ShieldIcon size={20}/>, "WireGuard Protocol", "3x faster than OpenVPN. Uses ChaCha20 encryption and Curve25519 key exchange."],
              [<ZapIcon size={20}/>, "Network Kill Switch", "If the VPN drops for any reason, all internet traffic is instantly blocked. Your real IP never leaks."],
              [<LockIcon size={20}/>, "DNS Leak Protection", "All DNS queries route through our private resolvers. Your ISP sees nothing."],
              [<GlobeIcon size={20}/>, "50+ Global Servers", "Bare-metal servers across 30+ countries. No shared cloud infrastructure."],
              [<ActivityIcon size={20}/>, "Split Tunneling", "Choose which apps route through the VPN and which use your regular connection."],
            ].map(([icon, title, desc]) => (
              <div key={title} className="feat-card">
                <div className="feat-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <hr className="divider"/>
      <div className="srv-bg" id="servers">
        <div className="sec">
          <div className="sec-label">Server Network</div>
          <h2 className="sec-h2">Bare metal.<br/>Every location.</h2>
          <p className="sec-sub">No rented AWS or Azure instances. Every server is owned hardware.</p>
          <div className="srv-grid">
            {SERVERS.map(s => (
              <div key={s.city} className="srv-card">
                <div className="srv-flag">{s.flag}</div>
                <div className="srv-info">
                  <div className="srv-city">{s.city}</div>
                  <div className="srv-cc">{s.cc}</div>
                </div>
                <div className="srv-meta">
                  <div className="srv-ping">{s.ping}ms</div>
                  <div className="srv-load">{s.load}% load</div>
                  <div className="srv-bar"><div className="srv-fill" style={{width:`${s.load}%`}}/></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <hr className="divider"/>
      <section id="pricing">
        <div className="sec">
          <div className="sec-label">Pricing</div>
          <h2 className="sec-h2">Transparent pricing.<br/>No dark patterns.</h2>
          <p className="sec-sub">No auto-renewing trials, no confusing tiers. Pick a plan, cancel anytime.</p>
          <div className="pricing-grid">
            {PLANS.map(p => (
              <div key={p.id} className={`plan ${p.highlight ? "hi" : ""}`}>
                {p.badge && <div className="plan-badge">{p.badge}</div>}
                <div className="plan-name">{p.name}</div>
                <div className="plan-price">
                  <span className="plan-dollar">${p.price}</span>
                  <span className="plan-per">{p.period}</span>
                </div>
                <div className="plan-billed">{p.billed || " "}</div>
                <ul className="plan-feats">
                  {p.features.map(f => (
                    <li key={f} className="plan-feat">
                      <span className="plan-feat-ck"><CheckIcon size={15}/></span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button className={`plan-btn ${p.highlight ? "plan-btn-g" : "plan-btn-o"}`} onClick={() => openAuth("signup")}>{p.cta}</button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <hr className="divider"/>
      <section id="faq">
        <div className="sec">
          <div className="sec-label">FAQ</div>
          <h2 className="sec-h2">Questions answered<br/>honestly.</h2>
          <div className="faq-list">
            {FAQS.map(([q,a], i) => (
              <div key={i} className={`faq-item ${openFaq === i ? "open" : ""}`}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {q}
                  <span className="faq-chevron"><ChevronDown size={17}/></span>
                </button>
                <div className="faq-a">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div>
              <div className="footer-brand-logo">
                <div className="footer-brand-lm"><ShieldIcon size={14}/></div>
                <span className="footer-brand-lt">VPNNull</span>
              </div>
              <p className="footer-brand-p">Serious privacy for people who mean it. No logs, no compromise, no exceptions. Incorporated in Panama.</p>
            </div>
            {[
              ["Product", ["Features","Pricing","Server Status","Download App","Changelog"]],
              ["Company", ["About","Blog","Transparency Report","No-Logs Audit","Careers"]],
              ["Legal", ["Privacy Policy","Terms of Service","DMCA Policy","GDPR","Cookie Policy"]],
            ].map(([title, links]) => (
              <div key={title} className="footer-col">
                <h4>{title}</h4>
                <ul>{links.map(l => <li key={l}><button className="footer-col-btn">{l}</button></li>)}</ul>
              </div>
            ))}
          </div>
          <div className="footer-bottom">
            <span className="footer-bottom-t">2026 VPNNull Inc. Panama. No-logs verified.</span>
            <span className="footer-bottom-t">WireGuard. AES-256. RAM-only servers.</span>
          </div>
        </div>
      </footer>
    </>
  );
}

// ── DASHBOARD ──
function Dashboard({ user, onLogout }) {
  const [connected, setConnected] = useState(false);
  const [serverIdx, setServerIdx] = useState(0);
  const [timer, setTimer] = useState(0);
  const [dl, setDl] = useState(0);
  const [ul, setUl] = useState(0);
  const [sub, setSub] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const timerRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    // Check subscription status
    const token = getToken();
    if (token) {
      fetch(`${API}/api/subscriptions/status`, {
        headers: { "Authorization": `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => { setSub(data); setLoadingSub(false); })
        .catch(() => setLoadingSub(false));
    } else {
      setLoadingSub(false);
    }
  }, []);

  useEffect(() => {
    if (connected) {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
      statsRef.current = setInterval(() => {
        setDl(+(Math.random() * 60 + 30).toFixed(1));
        setUl(+(Math.random() * 15 + 5).toFixed(1));
      }, 2000);
    } else {
      clearInterval(timerRef.current);
      clearInterval(statsRef.current);
      setTimer(0); setDl(0); setUl(0);
    }
    return () => { clearInterval(timerRef.current); clearInterval(statsRef.current); };
  }, [connected]);

  const fmt = s => [Math.floor(s/3600), Math.floor((s%3600)/60), s%60].map(n => String(n).padStart(2,"0")).join(":");
  const sv = SERVERS[serverIdx];
  const plan = PLANS.find(p => p.id === (sub?.plan || user.plan)) || PLANS[1];
  const hasActiveSub = sub && sub.status === "active";

  const downloadConfig = async () => {
    const token = getToken();
    try {
      const res = await fetch(`${API}/api/vpn/config`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vpnnull-${sv.city.toLowerCase().replace(" ","")}.conf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Config download failed", err);
    }
  };

  const openBillingPortal = async () => {
    const token = getToken();
    try {
      const res = await fetch(`${API}/api/subscriptions/portal`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error("Portal failed", err);
    }
  };

  const startCheckout = async (planId) => {
    const token = getToken();
    try {
      const res = await fetch(`${API}/api/subscriptions/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ plan: planId })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error("Checkout failed", err);
    }
  };

  return (
    <div className="dash">
      <div className="dash-top">
        <div className="dash-title">
          <h1>Hey, {user.name ? user.name.split(" ")[0] : "there"} 👋</h1>
          <p>Your encrypted connection dashboard — {user.email}</p>
        </div>
        <button className="btn-sm-ghost" onClick={onLogout}>Sign Out</button>
      </div>

      {!loadingSub && !hasActiveSub && (
        <div className="upgrade-banner">
          <h3>Activate Your Protection</h3>
          <p>Your account is ready. Subscribe to start using VPNNull and download your WireGuard config.</p>
          <button className="upgrade-btn" onClick={() => startCheckout("annual")}>
            <ShieldIcon size={16}/> Subscribe Now — $3.99/mo
          </button>
        </div>
      )}

      <div className={`vpn-card ${connected ? "on" : ""}`}>
        <div className="vpn-glow"/>
        <div className="vpn-top">
          <div className="vpn-status">
            <div className={`vpn-dot ${connected ? "on" : ""}`}/>
            <div>
              <div className="vpn-label">{connected ? `Protected · ${sv.flag} ${sv.city}` : "Not Connected"}</div>
              <div className="vpn-sublabel">{connected ? `IP hidden · ${sv.ping}ms · WireGuard ChaCha20` : "Your real IP address is visible"}</div>
            </div>
          </div>
          <div className="toggle-wrap">
            <span className="toggle-text">{connected ? "ON" : "OFF"}</span>
            <button className={`toggle ${connected ? "on" : "off"}`} onClick={() => hasActiveSub && setConnected(v => !v)}>
              <div className="toggle-thumb"/>
            </button>
          </div>
        </div>
        <div className="vpn-metrics">
          {[
            ["Session", connected ? fmt(timer) : "—", connected ? "g" : ""],
            ["Download", connected ? `${dl} Mbps` : "—", connected ? "g" : ""],
            ["Upload", connected ? `${ul} Mbps` : "—", connected ? "g" : ""],
            ["Encryption", "AES-256", "g"],
          ].map(([label, val, cls]) => (
            <div key={label} className="vpn-m">
              <div className="vpn-m-label">{label}</div>
              <div className={`vpn-m-val ${cls}`}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="dash-grid">
        <div className="dc">
          <h3><GlobeIcon size={16}/>Select Server</h3>
          <div className="sp-list">
            {SERVERS.slice(0, 6).map((s, i) => (
              <div key={s.city} className={`sp-opt ${i === serverIdx ? "active" : ""}`} onClick={() => { setServerIdx(i); if (connected) setConnected(false); }}>
                <span className="sp-flag">{s.flag}</span>
                <div className="sp-info">
                  <div className="sp-city">{s.city}, {s.cc}</div>
                  <div className="sp-ping">{s.ping}ms · {s.load}% load</div>
                </div>
                {i === serverIdx && <span className="sp-ck"><CheckIcon size={14}/></span>}
              </div>
            ))}
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          <div className="dc">
            <h3><LockIcon size={16}/>Subscription</h3>
            <div className="sub-rows">
              {loadingSub ? (
                <div style={{textAlign:"center",padding:"20px",color:"var(--mu2)"}}>Loading...</div>
              ) : (
                <>
                  {[
                    ["Plan", hasActiveSub ? plan.name : "None", hasActiveSub],
                    ["Status", hasActiveSub ? "Active" : "No subscription", hasActiveSub],
                    ["Devices", hasActiveSub ? plan.devices + " allowed" : "—", false],
                    ["Protocol", "WireGuard", false],
                    ["Kill Switch", "Enabled", true],
                  ].map(([k,v,g]) => (
                    <div key={k} className="sub-row">
                      <span className="sub-row-k">{k}</span>
                      <span className={`sub-row-v ${g ? "g" : ""}`}>{v}</span>
                    </div>
                  ))}
                  {hasActiveSub && (
                    <button className="btn-sm-ghost" style={{marginTop:8,width:"100%"}} onClick={openBillingPortal}>
                      Manage Billing →
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="dc">
            <h3><DownloadIcon size={16}/>WireGuard Config</h3>
            <div className="dl-box">
              <p>Download your config file and import it into WireGuard on any device — iOS, Android, macOS, Windows, or Linux.</p>
              {hasActiveSub ? (
                <button className="dl-btn" onClick={downloadConfig}>
                  <DownloadIcon size={14}/>
                  Download {sv.city} Config
                </button>
              ) : (
                <button className="dl-btn" style={{opacity:.5,cursor:"not-allowed"}}>
                  Subscribe to Download
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ROOT APP ──
export default function App() {
  const [user, setUserState] = useState(getUser());
  const [modal, setModal] = useState(null);

  // Check for Stripe success return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true" && user) {
      window.history.replaceState({}, "", "/");
    }
  }, [user]);

  const openAuth = tab => setModal(tab);
  const closeAuth = () => setModal(null);

  const login = userData => {
    setUser(userData);
    setUserState(userData);
    setModal(null);
  };

  const logout = () => {
    clearToken();
    setUserState(null);
  };

  const scrollTo = id => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior:"smooth" });
  };

  return (
    <>
      <style>{CSS}</style>
      <nav className="nav">
        <a className="nav-logo" href="/">
          <div className="nav-logo-mark"><ShieldIcon size={16}/></div>
          <span className="nav-logo-text">VPNNull</span>
        </a>
        {!user && (
          <ul className="nav-links">
            {["features","servers","pricing","faq"].map(id => (
              <li key={id}><button onClick={() => scrollTo(id)}>{id.charAt(0).toUpperCase()+id.slice(1)}</button></li>
            ))}
          </ul>
        )}
        <div className="nav-right">
          {user ? (
            <>
              <span style={{fontSize:13,color:"var(--mu2)"}}>{user.email}</span>
              <button className="btn-ghost" onClick={logout}>Sign Out</button>
            </>
          ) : (
            <>
              <button className="btn-ghost" onClick={() => openAuth("login")}>Log In</button>
              <button className="btn-green" onClick={() => openAuth("signup")}><ShieldIcon size={14}/>Get Protected</button>
            </>
          )}
        </div>
        <button className="hamburger" onClick={() => openAuth("signup")}><MenuIcon/></button>
      </nav>
      {user ? <Dashboard user={user} onLogout={logout}/> : <Landing openAuth={openAuth} scrollTo={scrollTo}/>}
      {modal && <AuthModal tab={modal} onClose={closeAuth} onLogin={login}/>}
    </>
  );
}